import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getUrlShort } from '../libs/DBApiHandler'
import { sendSlackNotification } from '../libs/CloudFunctionsApiHandler'
import { SlackChannels } from '../../../pure-js/enums/SlackChannels'

type BrokenLinkErrorType = 'invalid_hash' | 'missing_url' | 'fetch_error'

export const notifyBrokenLink = (hash: string, errorType: BrokenLinkErrorType): void => {
  const text = `*Broken short link detected*\n*Hash:* \`${hash}\`\n*Error:* ${errorType}`
  sendSlackNotification({ text, channel: SlackChannels.OPS_BROKEN_LINKS }).catch(() => undefined)
}

export const UrlShortRedirectPage = () => {
  const { hash } = useParams<{ hash: string }>() // Read "hash" from URL params
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    let isMounted = true

    const fetchOriginalUrl = async () => {
      try {
        if (!isMounted) return
        setIsLoading(true)
        if (!hash) {
          if (isMounted) {
            setError('Hash is missing')
            setIsLoading(false)
          }
          return
        }

        // Retrieve the original URL from Firestore
        const data = await getUrlShort(hash)

        if (!isMounted) return

        if (data) {
          const redirectUrl = data.url
          if (redirectUrl) {
            // Check if URL is internal or external
            if (redirectUrl.startsWith('/') || redirectUrl.startsWith(window.location.origin)) {
              // For internal links, use React Router navigation
              const internalPath = redirectUrl.startsWith('/')
                ? redirectUrl
                : redirectUrl.replace(window.location.origin, '')
              navigate(internalPath)
            } else {
              // For external links, we still need to use window.location
              window.location.href = redirectUrl
            }
          } else {
            notifyBrokenLink(hash, 'missing_url')
            setError('Original URL not found')
          }
        } else {
          notifyBrokenLink(hash, 'invalid_hash')
          setError('Invalid hash')
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching original URL:', err)
          notifyBrokenLink(hash ?? '', 'fetch_error')
          setError('Failed to fetch data')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchOriginalUrl()

    return () => {
      isMounted = false
    }
  }, [hash, navigate])

  return (
    <div className="redirect-container">
      {isLoading ? (
        <div className="spinner">Redirecting...</div>
      ) : error ? (
        <p className="error-state">{error}</p>
      ) : null}
    </div>
  )
}
